import React, { useState, useCallback, useEffect} from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { useRouter } from "next/router";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { ENDPOINTS } from "@/config/endpoints";
import { pushCleverTapEvent } from "../pushCleverTapEvent";
const schema = () => {
  return yup.object({
    Name: yup.string().required("This field is required."),
    Email: yup
      .string()
      .required("This field is required.")
      .email("Email is not valid"),
    Phone: yup
      .string()
      .required("This field is required.")
      .matches(/^\d{10}$/, "Phone number must be 10 digits")
      .test(
        "not-starting-with-0-1-2-3",
        "Phone number must not start with 0,1, 2, 3",
        (value) => value && !/^[0123]/.test(value)
      ),
      LoanAmount: yup.number()
      .required("This field is required.")
      .nullable() // Allows the value to be null or empty initially
      .transform((value, originalValue) =>
        originalValue.trim() === "" ? null : value // Converts empty strings to null
      )
      .test(
        "max-value",
        "Loan amount can not exceed ₹50 crore",
        (value) => value === null || value <= 500000000
      ),
      GSTNo: yup.string()
        .test(
          "gst-format",
          "Invalid GST Number format",
          (value) => !value || /^[0-9]{2}[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9a-zA-Z]{1}[Zz][0-9a-zA-Z]{1}$/.test(value)
        ),
      Consent: yup.boolean().oneOf([true], "To proceed, you need to accept our Terms & Privacy Policy.")
  });
};

const BusinessLoanForm = ({pageTitle}) => {
  const router = useRouter();
  const validationSchema = schema();
  const [ThankyouRes, setThankyouRes] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [errMSG, setErrMSG] = useState(false);
  const [nameClicked, setNameClicked] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm({
    mode: "all",
    resolver: yupResolver(validationSchema),
    defaultValues: {
      Consent: true,
    }
  });

  const watchAllFields = watch();

  const options = [
    { label: "0-25 L", value: "0-25 L" },
    { label: "25-50 L", value: "25-50 L" },
    { label: "50 L-1 Cr", value: "50 L-1 Cr" },
    { label: "Above 1 Cr", value: "Above 1 Cr" },
  ];
  const [activeOption, setActiveOption] = useState(options[0].value);

  const handleClick = (value) => {
    setActiveOption(value);
  }
  const handleNameClick = () => {
    if (!nameClicked) {
      pushCleverTapEvent("Business_Loan_Form_Start", {
        "PageName": 'Business_Loan',
      });
      setNameClicked(true);
    }
  };

  // Floating label class
  const floatingLabelClass = (fieldValue) => `
    absolute left-3 bg-white px-[5px] text-sm text-gray-500 font-normal transition-all pointer-events-none
    ${
      fieldValue
        ? "top-[-8px] text-xs text-black"
        : "top-2 peer-focus:top-[-8px] peer-focus:text-xs peer-focus:text-black"
    }
  `;

  const onFormSubmit = useCallback(async (values, gReCaptchaToken) => {
    const body = {
      formType: "BusinessLoanForm",
      name: values.Name,
      email: values.Email,
      phoneNumber: values.Phone,
      sourceUrl: window.location.href,
      leadSource: "BL_LP",
      turnover: activeOption,
      exporterGstNo: values.GSTNo ? values.GSTNo : "" ,
      requiredLoanAmnt: values.LoanAmount,
      campaignName: "BL_Landing_Page",
    };
    let config = {
      method: "post",
      url: ENDPOINTS.BUSINESS_LOAN,
      headers: {
        "Content-Type": "application/json",
        "g-recaptcha-response": gReCaptchaToken,
      },
      data: body,
    };
    try {
      const response = await axios(config);
      if (response.status == 200) {
        gtag('event', 'businessLoan', {
          'event_category': 'businessLoan',
          'event_label': 'businessLoan'
      });
        pushCleverTapEvent("Business_Loan_Form_Submit", { "PageName": 'Business_Loan', ...body });  
         setThankyouRes(true);
        setTimeout(() => {
          setThankyouRes(false);
          router.push("/thankyouresponse");
        }, 3000);
      }
    } catch (err) {
      setErrMSG(true);
      console.log("error", err);
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    }
  }, [activeOption, router]);

  const handleCaptchaSubmit = useCallback(
    (values) => {
      if (!executeRecaptcha) {
        return false;
      }
      executeRecaptcha("enquiryFormSubmit").then((gReCaptchaToken) => {
        onFormSubmit(values, gReCaptchaToken);
      });
    },
    [executeRecaptcha, onFormSubmit]
  );

  return (
    <div className="sm:w-full pb-[45px] z-0 block">
      <div className="bg-white sm:mt-2 shadow-icon-btn-shadow rounded-lg py-5 px-3 sm:px-2 lg:w-full w-full ml-auto md:relative md:m-auto md:mb-3 border-#FFFFFF">
      {!pageTitle ? (
      <span className="text-2xl text-[#012A5B] font-normal sm:text-xl">
       Check Your Eligibility
      </span>
      ) : (
     <span className="text-lg text-[#2C2C2C] sm:text-xl">
      Check your <span className="font-medium">Eligibility</span> and get funded
     </span>
     )}
        <form
          className=" mt-3 flex flex-wrap gap-3"
          onSubmit={handleSubmit(handleCaptchaSubmit)}
        >
          {/* Row 1: Name and Email */}
          <div className="relative w-[calc(50%-1rem)] sm:w-full">
            <input
              {...register("Name")}
              id="name"
              type="text"
              placeholder=" "
              onClick={handleNameClick}
              aria-required="true"
              className="peer block w-full rounded-md border border-bordercolor p-2 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none"
            />
            <label
              htmlFor="name"
              className={floatingLabelClass(watchAllFields.Name)}
            >
              First & Last Name as per PAN Card*
            </label>
            {errors.Name && (
              <p className="text-red-500 text-xs mt-1">{errors.Name?.message}</p>
            )}
          </div>

          <div className="relative w-[calc(50%-1rem)] sm:w-full">
            <input
              {...register("Email")}
              id="email"
              type="email"
              placeholder=" "
              aria-required="true"
              className="peer block w-full rounded-md border border-bordercolor p-2 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none"
            />
            <label
              htmlFor="email"
              className={floatingLabelClass(watchAllFields.Email)}
            >
              Email Address*
            </label>
            {errors.Email && (
              <p className="text-red-500 text-xs mt-1">{errors.Email?.message}</p>
            )}
          </div>

          {/* Row 2: Mobile Number and Annual Turnover */}
          <div className="relative w-[calc(50%-1rem)] sm:w-full">
            <input
              {...register("Phone")}
              id="phone"
              type="tel"
              maxLength={10}
              placeholder=" "
              aria-required="true"
              pattern="[4-9][0-9]{9}"
              className="peer block w-full rounded-md border border-bordercolor p-2 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none"
            />
            <label
              htmlFor="phone"
              className={floatingLabelClass(watchAllFields.Phone)}
            >
              Mobile Number*
            </label>
            {errors.Phone && (
              <p className="text-red-500 text-xs mt-1">{errors.Phone?.message}</p>
            )}
          </div>

          <div className="relative w-[calc(50%-1rem)] sm:w-full">
            <div className="flex justify-between w-full rounded-md border border-bordercolor px-2 py-1 text-sm text-secondary gap-1">
              {options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleClick(option.value)}
                  className={`cursor-pointer rounded-md text-xs whitespace-nowrap transition-all ${
                    activeOption === option.value
                      ? "bg-[#D5E8FF] border border-[#0362C2] px-1 py-[6px] text-[#0362C2] font-medium"
                      : "bg-white py-[6px]"
                  }`}
                >
                  {option.label}
                </div>
              ))}
            </div>
            <input
              type="text"
              id="turnover"
              value={activeOption}
              className="sr-only"
              readOnly
              tabIndex="-1"
              aria-required="true"
            />
            <label htmlFor="turnover" className="absolute left-3 top-[-8px] bg-white px-1 text-xs text-black font-normal pointer-events-none">
              Annual Turnover*
            </label>
          </div>

          {/* Row 3: GST Number and Required Loan Amount */}
          <div className="relative w-[calc(50%-1rem)] sm:w-full">
            <input
              {...register("GSTNo")}
              id="gst"
              type="text"
              maxLength={15}
              placeholder=" "
              pattern="^[0-9]{2}[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9a-zA-Z]{1}[Zz][0-9a-zA-Z]{1}$"
              className="peer block w-full rounded-md border border-bordercolor p-2 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none"
            />
            <label
              htmlFor="gst"
              className={floatingLabelClass(watchAllFields.GSTNo)}
            >
              GST Number (Optional)
            </label>
          </div>

          <div className="relative w-[calc(50%-1rem)] sm:w-full">
            <input
              {...register("LoanAmount")}
              id="loanAmount"
              type="number"
              max={500000000}
              placeholder=" "
              aria-required="true"
              className="peer block w-full rounded-md border border-bordercolor p-2 text-sm text-secondary focus:border-[#288BED] focus:ring-[#555] focus:outline-none"
            />
            <label
              htmlFor="loanAmount"
              className={floatingLabelClass(watchAllFields.LoanAmount)}
            >
              Required Loan Amount*
            </label>
            {errors.LoanAmount && (
              <p className="text-red-500 text-xs mt-1">{errors.LoanAmount?.message}</p>
            )}
          </div>

          {/* Consent Checkbox */}
          <div className="w-full flex items-start">
            <input
              id="consent"
              type="checkbox"
              {...register("Consent")}
              className="w-3 h-3 cursor-pointer accent-[#022B5C]"
              aria-required="true"
            />
            <label
              htmlFor="consent"
              className="ml-2 block text-sm text-secondary cursor-pointer select-none"
            >
              I agree to the{" "}
              <a
                href="/terms-and-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#0362C2] hover:text-[#022B5C]"
              >
                Terms of Use
              </a>{" "}
              &{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#0362C2] hover:text-[#022B5C]"
              >
                Privacy Policy
              </a>
              .
            </label>
          </div>
          {errors.Consent && (
            <p className="text-red-500 text-xs w-full mt-[-4px]">
              {errors.Consent.message}
            </p>
          )}

          {/* Submit Button */}
          <div className="w-full mt-xsp">
            <button
              className="h-12 px-3 rounded-md text-white text-base font-medium w-full bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C]"
              type="submit"
            >
              {pageTitle ? 'Apply Now': 'Submit'}
            </button>
          </div>

          {/* <div>
                {errMSG && (
                  <div className="error-message">An error occurred!</div>
                )}
              </div> */}
        </form>
        {ThankyouRes ? (
          <>
            <div className="text-green text-center">
              <p className="text-sm font-medium mt-3">
                Successfull Submission : Thank you for your interest.{" "}
              </p>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default BusinessLoanForm;
